// Numbers for the extra Dashboard widgets (DashboardWidgets.js) that the stat tiles don't
// already show: expectancy, day win rate, current streaks and the largest drawdown.
import { netPnl, tradingDayKey } from './pnl';

const byTime = (trades) => [...trades].filter((t) => t.date).sort((a, b) => new Date(a.date) - new Date(b.date));

export const expectancy = (trades) => (trades.length ? trades.reduce((s, t) => s + netPnl(t), 0) / trades.length : null);

// Net P&L per trading day, oldest first.
export function dayNets(trades) {
  const days = new Map();
  for (const t of byTime(trades)) {
    const key = tradingDayKey(t.date);
    days.set(key, (days.get(key) || 0) + netPnl(t));
  }
  return [...days.entries()].map(([key, net]) => ({ key, net }));
}

export function dayWinRate(trades) {
  const days = dayNets(trades);
  if (!days.length) return null;
  return { rate: (days.filter((d) => d.net > 0).length / days.length) * 100, green: days.filter((d) => d.net > 0).length, days: days.length };
}

// How many winning (or losing) days and trades in a row, counting back from the latest.
// Breakeven days and trades end a streak.
function streakOf(values) {
  const last = values[values.length - 1];
  if (last === undefined || last === 0) return { count: 0, winning: null };
  const winning = last > 0;
  let count = 0;
  for (let i = values.length - 1; i >= 0 && (winning ? values[i] > 0 : values[i] < 0); i -= 1) count += 1;
  return { count, winning };
}
export const currentStreaks = (trades) => ({ days: streakOf(dayNets(trades).map((d) => d.net)), trades: streakOf(byTime(trades).map(netPnl)) });

// The biggest drop from a high point of running P&L, trade by trade, and the day it bottomed.
export function maxDrawdown(trades) {
  let running = 0;
  let peak = 0;
  let worst = { amount: 0, day: null };
  for (const t of byTime(trades)) {
    running += netPnl(t);
    peak = Math.max(peak, running);
    if (running - peak < worst.amount) worst = { amount: running - peak, day: tradingDayKey(t.date) };
  }
  return worst;
}

// The longest winning and losing runs of days and of trades. Breakeven ends a run.
function longest(values) {
  let win = 0;
  let loss = 0;
  let w = 0;
  let l = 0;
  for (const v of values) {
    w = v > 0 ? w + 1 : 0;
    l = v < 0 ? l + 1 : 0;
    win = Math.max(win, w);
    loss = Math.max(loss, l);
  }
  return { win, loss };
}
export const longestStreaks = (trades) => ({ days: longest(dayNets(trades).map((d) => d.net)), trades: longest(byTime(trades).map(netPnl)) });

// How far below its high the running P&L was after each trade (0 or less), and where it is now.
export function drawdownSeries(trades) {
  let running = 0;
  let peak = 0;
  const points = byTime(trades).map((t) => {
    running += netPnl(t);
    peak = Math.max(peak, running);
    return running - peak;
  });
  return { points, now: points.length ? points[points.length - 1] : 0 };
}

// What wins and losses add to an average trade: wins give +x per trade, losses take -y.
export function expectancyParts(trades) {
  if (!trades.length) return null;
  const nets = trades.map(netPnl);
  return { fromWins: nets.filter((n) => n > 0).reduce((s, n) => s + n, 0) / nets.length, fromLosses: nets.filter((n) => n < 0).reduce((s, n) => s + n, 0) / nets.length };
}
